import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { actOnStaysListing, getStaysListing, StaysApiError } from '../../../../api/stays-client';
import type { StaysListing, StaysListingAction } from '../../../../interfaces/stays';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { useStaysEntitlement } from '../../../hooks/use-stays-entitlement';
import { AppButton } from '../../../button/app-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { StatusPill } from '../../../status-pill/status-pill';
import { BackBar, BackButton } from '../../../layout/back-button';
import { ListSkeleton, LoadError, skeletonHeight } from '../dashboard-shared-components/load-state';
import { ActionWithReason } from '../dashboard-shared-components/disabled-reason';
import { StateSwitch, StatusLabel } from '../../../../motion';
import toast from '../../../../utils/toast';
import { formatDate } from '../../../../function-utils/locale';
import { deviceZone, zoneLabel } from '../../../../function-utils/building-time';
import { ListingBasicsModal } from './listing-basics-modal';
import { StaysReservationsCard } from '../../stays/stays-reservations-card';
import { AddStayFlow } from '../../stays/add-stay-modal';
import { ContentTransition } from '../../../route-transition/route-transition';
import { headOf, listingSectionHref, stayHref, StaysListingSection } from '../../stays/listing/stays-listing-routes';
import { StaysListingTabs } from '../../stays/listing/stays-listing-tabs';
import { ListingBuildingLinkCard } from '../../stays/listing/listing-building-link-card';
import { StaysOverviewCards } from '../../stays/overview/stays-overview';
import { StaysReservationsTable } from '../../stays/reservations/stays-reservations';
import { StaysCalendar } from '../../stays/calendar/stays-calendar';
import { CheckinTab } from '../../stays/checkin/checkin-tab';
import { GuideEditorShell } from '../../stays/guide/guide-editor-shell';
import { CalendarSyncTab } from '../../stays/sync/calendar-sync-tab';
import { AlertsTab } from '../../stays/alerts/alerts-settings-page';
import { NewReservationPage } from '../../stays/reservation-form/new-reservation-page';
import { palette, radius, spacing, status as statusTone, typography } from '../../../../style-utils/theme';
import {
  ATTENTION_LABEL,
  ATTENTION_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  attentionCopy,
  countryName,
  guestsText,
  refusalText,
  unitLine,
} from './stays-labels';
import { useDeskInPlan } from '../../../hooks/use-desk-in-plan';
import { w } from '../../../../stays/wording';
import {
  ActionRow,
  AttentionBanner,
  Card,
  CardTitle,
  DetailHeader,
  DetailSub,
  DetailTitle,
  DetailTitleBlock,
  Empty,
  EmptyText,
  EmptyTitle,
  Pills,
  RetiredLine,
  Screen,
  SpecGrid,
  SpecItem,
  SpecLabel,
  SpecValue,
} from './dashboard-stays.styled';

/**
 * ONE STAYS LISTING (ai-plans/stays plan S1, screen S-3). /dashboard/stays/:id.
 *
 * The listing's basics, its status and why it may not be bookable, and — for
 * stays:promote — Edit basics plus the one or two actions its status allows:
 * draft → Make live / Retire; live → Pause / Retire; paused → Resume / Retire;
 * retired → nothing ("Promote the unit again to list it").
 *
 * Every action carries the listing's version. A listing someone else changed
 * meanwhile answers 409 STAYS_LISTING_CHANGED with the current listing: the
 * page says so and shows it — never a silent overwrite.
 *
 * Make live / Resume are disabled, with the reason visible, while the listing
 * reads "Needs attention" — the server would refuse them for the same reason.
 *
 * PLAN S4 — THE LISTING SHELL. With a `section` (stays/listing/
 * stays-listing-shell.tsx) the same page becomes the shell: the header gains
 * "New stay", a tab strip ("Listing sections") follows, and the section
 * renders in its own crossfade. The Overview section is this page's S1/S3
 * content unchanged, followed by the S4 overview cards — the bare path
 * /dashboard/stays/<id> renders it, with no redirect. Without a `section` the
 * page renders exactly as S1/S3 built it.
 */

const policyTime = (time: string, zone: string, desk = true): string => {
  const label = zoneLabel(zone);
  const whose = w('timeOfPlace', desk);

  return label && zone !== deviceZone() ? `${time} ${whose} (${label})` : `${time} ${whose}`;
};

type Confirming = 'pause' | 'retire' | null;

/* Plan S4, shell mode: the header's actions ("Open unit", "New stay"). */
const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${spacing.xs};
  min-width: 0;
`;

/* A paused listing still takes stays (OQ-3): a notice, not a warning. */
const PausedNotice = styled.p`
  margin: 0;
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${statusTone.neutral.bg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${statusTone.neutral.fg};
`;

/* The section layer: the cards inside it keep the screen's own rhythm. */
const SECTION_LAYER_STYLE: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.md,
  minWidth: 0,
};

/** Why "New stay" cannot be used yet, or null when it can (OQ-3: paused takes stays). */
const newStayReason = (status: StaysListing['status'], desk = true): string | null =>
  status === 'draft'
    ? 'This listing is a draft. Make it live to add stays.'
    : status === 'retired'
    ? `This listing is retired. ${w('promoteAgainForStays', desk)}`
    : null;

export interface StaysListingDetailProps {
  listingId: string;
  /**
   * Plan S4 — the listing shell (stays/listing/stays-listing-shell.tsx). Set,
   * the page renders the header, the tabs and that section; absent, it renders
   * exactly as plan S1/S3 built it.
   */
  section?: StaysListingSection;
  /** Section 'stays' only: open this stay's edit modal (the deep link). */
  reservationId?: string | null;
}

export const StaysListingDetail: React.FC<StaysListingDetailProps> = ({ listingId, section, reservationId = null }) => {
  const shell = section !== undefined;
  const navigate = useNavigate();
  const { permissions, user } = useAuth();
  // IS5 (IS5-T1): editing and pause / resume / retire are stays:listing_edit —
  // a co-host holds it for the listings shared with them.
  const canEditListing = can(permissions, PERMISSIONS.STAYS_LISTING_EDIT);
  // Independent Stays IS2: property words without Desk.
  const desk = useDeskInPlan();
  // Plan S3: this listing's stays; adding one, its link, cancelling — stays:manage.
  const canManage = can(permissions, PERMISSIONS.STAYS_MANAGE);
  const { state: ent } = useStaysEntitlement({ enabled: can(permissions, PERMISSIONS.STAYS_READ) });
  const [adding, setAdding] = useState(false);
  const [staysReload, setStaysReload] = useState(0);
  const [staysToday, setStaysToday] = useState<string | null>(null);

  const [listing, setListing] = useState<StaysListing | null>(null);
  const [state, setState] = useState<'loading' | 'error' | 'notfound' | 'off' | 'ready'>('loading');
  const [busy, setBusy] = useState<StaysListingAction | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [editing, setEditing] = useState(false);
  const inFlight = useRef(false);
  const seq = useRef(0);

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    getStaysListing(listingId)
      .then(l => {
        if (seq.current !== mine) return;
        setListing(l);
        setState('ready');
      })
      .catch(error => {
        if (seq.current !== mine) return;
        if (error instanceof StaysApiError && error.code === 'STAYS_NOT_ENABLED') setState('off');
        else if (error instanceof StaysApiError && error.status === 404) setState('notfound');
        else setState(current => (current === 'ready' ? 'ready' : 'error'));
      });
  }, [listingId]);

  useEffect(() => {
    setState('loading');
    load();
  }, [load]);

  const run = async (action: StaysListingAction) => {
    if (!listing || inFlight.current) return;
    inFlight.current = true;
    setBusy(action);
    try {
      const saved = await actOnStaysListing(listing.id, action, listing.version);
      setListing(saved);
      toast.success(
        action === 'activate'
          ? 'Listing is live.'
          : action === 'pause'
          ? 'Listing paused.'
          : action === 'resume'
          ? 'Listing is live again.'
          : 'Listing retired.'
      );
    } catch (error) {
      const err = error as StaysApiError;
      if (err instanceof StaysApiError && err.code === 'STAYS_LISTING_CHANGED') {
        toast.error('This listing changed since you opened it. It has been read again.');
        if (err.extra.listing) setListing(err.extra.listing as StaysListing);
        else load();
      } else {
        toast.error(refusalText(err, 'Could not change the listing. Try again.'));
        load();
      }
    } finally {
      inFlight.current = false;
      setBusy(null);
    }
  };

  const view: 'loading' | 'error' | 'empty' | 'ready' =
    state === 'loading' ? 'loading' : state === 'error' ? 'error' : state === 'ready' ? 'ready' : 'empty';

  const l = listing;
  const blockedReason = l && l.attention ? attentionCopy(l.attention, l, ent?.policy) : null;
  const head = useMemo(() => (listing ? headOf(listing) : null), [listing]);
  // S3's "Add a stay": a live listing; in the S4 shell a paused one too (OQ-3).
  const canAddHere = !!l && canManage && (l.status === 'active' || (shell && l.status === 'paused'));

  const titleBlock = l ? (
    <DetailTitleBlock>
      <DetailTitle>{l.display_name}</DetailTitle>
      <DetailSub>{unitLine(l, desk)}</DetailSub>
      <Pills>
        <StatusPill tone={STATUS_TONE[l.status]} dot>
          {STATUS_LABEL[l.status]}
        </StatusPill>
        {l.attention ? (
          <StatusPill tone={ATTENTION_TONE} dot>
            {ATTENTION_LABEL}
          </StatusPill>
        ) : null}
      </Pills>
    </DetailTitleBlock>
  ) : null;

  const openUnit = l ? (
    <AppButton
      variant="ghost"
      onClick={() => navigate(desk ? `/dashboard/units/${l.unit_id}` : '/dashboard/stays/properties')}
    >
      {w('openUnit', desk)}
    </AppButton>
  ) : null;

  /* Plan S1's attention banner and basics, and plan S3's stays card: the
     listing page as it was, and the shell's Overview section. */
  const overviewBody = l ? (
    <>
      {l.attention ? (
        <AttentionBanner role="status" data-attention={l.attention}>
          {attentionCopy(l.attention, l, ent?.policy)}
        </AttentionBanner>
      ) : null}

      <Card aria-labelledby="stays-basics-title">
        <CardTitle id="stays-basics-title">Listing basics</CardTitle>
        <SpecGrid>
          <SpecItem>
            <SpecLabel>Name shown to guests</SpecLabel>
            <SpecValue>{l.display_name}</SpecValue>
          </SpecItem>
          <SpecItem>
            <SpecLabel>Country</SpecLabel>
            <SpecValue>{countryName(l.country_code)}</SpecValue>
          </SpecItem>
          <SpecItem>
            <SpecLabel>Currency</SpecLabel>
            <SpecValue>{l.currency}</SpecValue>
          </SpecItem>
          <SpecItem>
            <SpecLabel>Check-in from</SpecLabel>
            <SpecValue data-testid="stays-check-in">{policyTime(l.check_in_time, l.building_timezone, desk)}</SpecValue>
          </SpecItem>
          <SpecItem>
            <SpecLabel>Check-out by</SpecLabel>
            <SpecValue data-testid="stays-check-out">{policyTime(l.check_out_time, l.building_timezone, desk)}</SpecValue>
          </SpecItem>
          <SpecItem>
            <SpecLabel>Guests</SpecLabel>
            <SpecValue>{guestsText(l.max_guests)}</SpecValue>
          </SpecItem>
          <SpecItem>
            <SpecLabel>Host</SpecLabel>
            <SpecValue>{l.host?.name || '—'}</SpecValue>
          </SpecItem>
          <SpecItem>
            <SpecLabel>Promoted</SpecLabel>
            <SpecValue>
              {l.promoted_at ? (
                <span data-at={l.promoted_at}>
                  {formatDate(l.promoted_at, { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              ) : (
                '—'
              )}
            </SpecValue>
          </SpecItem>
        </SpecGrid>

        {l.status === 'retired' ? (
          <RetiredLine>
            {l.retired_at ? (
              <>
                Retired on{' '}
                <span data-at={l.retired_at}>
                  {formatDate(l.retired_at, { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
                . {w('promoteAgain', desk)}
              </>
            ) : (
              `Retired. ${w('promoteAgain', desk)}`
            )}
          </RetiredLine>
        ) : canEditListing ? (
          <ActionRow>
            {l.can.edit ? (
              <AppButton variant="secondary" onClick={() => setEditing(true)}>
                Edit basics
              </AppButton>
            ) : null}
            {l.can.activate ? (
              <ActionWithReason reason={blockedReason}>
                {describedBy => (
                  <AppButton
                    disabled={!!busy || !!blockedReason}
                    aria-describedby={describedBy}
                    onClick={() => void run('activate')}
                  >
                    <StatusLabel state={busy === 'activate' ? 'busy' : 'idle'} labels={{ idle: 'Make live', busy: 'Making live…' }} />
                  </AppButton>
                )}
              </ActionWithReason>
            ) : null}
            {l.can.pause ? (
              <AppButton variant="secondary" disabled={!!busy} onClick={() => setConfirming('pause')}>
                Pause
              </AppButton>
            ) : null}
            {l.can.resume ? (
              <ActionWithReason reason={blockedReason}>
                {describedBy => (
                  <AppButton
                    disabled={!!busy || !!blockedReason}
                    aria-describedby={describedBy}
                    onClick={() => void run('resume')}
                  >
                    <StatusLabel state={busy === 'resume' ? 'busy' : 'idle'} labels={{ idle: 'Resume', busy: 'Resuming…' }} />
                  </AppButton>
                )}
              </ActionWithReason>
            ) : null}
            {l.can.retire ? (
              <AppButton variant="ghost" disabled={!!busy} onClick={() => setConfirming('retire')}>
                Retire
              </AppButton>
            ) : null}
          </ActionRow>
        ) : null}
      </Card>

      {/* Independent Stays IS7: this place linked to its building's front desk. */}
      {user?.stays_building_link === true && canManage && l.status !== 'retired' ? <ListingBuildingLinkCard listingId={l.id} /> : null}

      <StaysReservationsCard
        listingId={l.id}
        policy={ent?.policy}
        canManage={canManage}
        reloadKey={staysReload}
        onToday={setStaysToday}
        emptyText="No stays booked for this listing yet."
        action={
          canAddHere ? (
            <AppButton variant="secondary" size="compact" aria-label={`Add a stay to ${l.display_name}`} onClick={() => setAdding(true)}>
              Add a stay
            </AppButton>
          ) : null
        }
      />
    </>
  ) : null;

  /* Plan S4: one section of the shell, inside its own crossfade. */
  const sectionBody = (key: StaysListingSection): React.ReactNode => {
    if (!l || !head) return null;
    switch (key) {
      case 'stays':
        return <StaysReservationsTable listing={head} policy={ent?.policy} canManage={canManage} reservationId={reservationId} />;
      case 'calendar':
        return <StaysCalendar listing={head} policy={ent?.policy} canManage={canManage} />;
      case 'check-in':
        return <CheckinTab listing={head} canManage={canManage} />;
      case 'guide':
        return <GuideEditorShell listing={head} canManage={canManage} />;
      case 'sync':
        return <CalendarSyncTab listing={head} canManage={canManage} />;
      case 'alerts':
        return <AlertsTab listing={head} canManage={canManage} />;
      case 'new':
        return canManage ? (
          <NewReservationPage listing={head} policy={ent?.policy} />
        ) : (
          <Empty role="status">
            <EmptyText>Your role can see this listing&apos;s stays but not add them.</EmptyText>
          </Empty>
        );
      case 'overview':
      default:
        return (
          <>
            {overviewBody}
            <StaysOverviewCards
              listingId={l.id}
              policy={ent?.policy}
              canManage={canManage}
              reloadKey={staysReload}
              onOpenStay={id => navigate(stayHref(l.id, id))}
            />
          </>
        );
    }
  };

  const shellBody =
    l && section ? (
      <>
        <DetailHeader>
          {titleBlock}
          <HeaderActions>
            {openUnit}
            {canManage ? (
              <ActionWithReason reason={newStayReason(l.status, desk)}>
                {describedBy => (
                  <AppButton
                    disabled={!!newStayReason(l.status, desk)}
                    aria-describedby={describedBy}
                    onClick={() => navigate(listingSectionHref(l.id, 'new'))}
                  >
                    New stay
                  </AppButton>
                )}
              </ActionWithReason>
            ) : null}
          </HeaderActions>
        </DetailHeader>

        {l.status === 'paused' ? (
          <PausedNotice role="status" data-testid="stays-paused-notice">
            This listing is paused. You can still record stays.
          </PausedNotice>
        ) : null}

        <StaysListingTabs listingId={l.id} active={section} />

        <ContentTransition contentKey={section} direction="none" style={SECTION_LAYER_STYLE}>
          {sectionBody(section)}
        </ContentTransition>
      </>
    ) : null;

  return (
    <Screen>
      {/* New stay carries its own "Back to stays" (to this listing's Stays tab):
          two back controls would differ only by a capital letter. */}
      {section === 'new' ? null : (
        <BackBar>
          <BackButton to="/dashboard/stays" label="Back to Stays" />
        </BackBar>
      )}

      <StateSwitch state={view} minHeight={skeletonHeight(3, 72)}>
        {view === 'loading' ? (
          <ListSkeleton rows={3} rowHeight={72} label="listing" />
        ) : view === 'error' ? (
          <LoadError message="Could not load this listing." onRetry={load} />
        ) : view === 'empty' || !l ? (
          <Empty role="status">
            {state === 'off' ? (
              <>
                <EmptyTitle>Lobbero Stays is not switched on for your organisation</EmptyTitle>
                <EmptyText>Stays lets you list {w('units', desk)} for short-term guests. Ask Lobbero to switch it on.</EmptyText>
              </>
            ) : (
              <>
                <EmptyTitle>This listing could not be found.</EmptyTitle>
                <EmptyText>{w('notInScope', desk)}</EmptyText>
              </>
            )}
          </Empty>
        ) : shell ? (
          shellBody
        ) : (
          <>
            <DetailHeader>
              {titleBlock}
              {openUnit}
            </DetailHeader>

            {overviewBody}
          </>
        )}
      </StateSwitch>
      {l && canManage ? (
        <AddStayFlow
          listing={adding ? l : null}
          today={staysToday}
          onClose={() => setAdding(false)}
          onSaved={() => setStaysReload(n => n + 1)}
        />
      ) : null}

      <ConfirmDialog
        open={confirming === 'pause'}
        title="Pause this listing?"
        message={`${l?.display_name || 'This listing'} stays on Stays but takes no new stays until you resume it.`}
        primaryLabel="Pause listing"
        dismissLabel="Keep it live"
        onPrimary={async () => {
          await run('pause');
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'retire'}
        title="Retire this listing?"
        message={`${l?.display_name || 'This listing'} leaves Stays. Its history is kept, and the unit can be promoted again later.`}
        primaryLabel="Retire listing"
        primaryVariant="destructive"
        dismissLabel="Cancel"
        onPrimary={async () => {
          await run('retire');
          setConfirming(null);
        }}
        onDismiss={() => setConfirming(null)}
      />
      {l ? (
        <ListingBasicsModal
          open={editing}
          mode="edit"
          listing={l}
          onClose={() => setEditing(false)}
          onSaved={saved => setListing(saved)}
        />
      ) : null}
    </Screen>
  );
};
